/**
 * AB test definition registry.
 *
 * `defineAbTest` declares an AB test with its PostHog flag key and durability tier.
 *
 * The durability tier controls how the assignment is persisted and restored:
 * - `"none"`:  live PostHog evaluation only, no persistence.
 * - `"flow"`:  stored in the Zustand signup-flow store for the current session.
 * - `"user"`:  written to DB table `ab_test_assignment` and restored on AC return so original variant persists.
 */

export const AB_TEST_VARIANTS = [ "test", "control" ] as const;

export type AbTestVariant = typeof AB_TEST_VARIANTS[number];

export const isAbTestVariant = (value: unknown): value is AbTestVariant =>
  typeof value === "string" && (AB_TEST_VARIANTS as readonly string[]).includes(value);

export const parseAbTestVariant = (value: unknown): AbTestVariant | undefined =>
  isAbTestVariant(value) ? value : undefined;

export type Durability = "none" | "flow" | "user";

/** Flag key to variant value (e.g. `{ "shredded-recipes-relaunch": "test" }`). */
export type TAbTestAssignments = Record<string, string>;

export interface AbTestDefinition {
  /** PostHog feature-flag key. */
  key: string;
  /** How the assignment is persisted (see above). */
  durability: Durability;
  /** Flow form names where the AB test is force-disabled. */
  excludeForms?: ReadonlySet<string>;
}

/** All registered AB tests. User-durable tests are auto-registered. */
const AB_TEST_REGISTRY: AbTestDefinition[] = [];

/**
 * Declares an AB test definition. User-durable tests (`durability: "user"`)
 * are automatically registered so `getUserDurableAbTests` can read them.
 */
export function defineAbTest(
  config: AbTestDefinition,
): AbTestDefinition {
  if (config.durability === "user") {
    AB_TEST_REGISTRY.push(config);
  }
  return config;
}

/**
 * Builds a `{ flagKey: variantString }` map for all user-durable AB tests
 * by reading their current values via `getFlag`.
 * Tests whose `excludeForms` contains `formName` are skipped.
 * Returns `undefined` when no assignments are available.
 */
export function getUserDurableAbTests(
  getFlag: (key: string) => string | boolean | undefined,
  formName?: string,
): TAbTestAssignments | undefined {
  const assignments: TAbTestAssignments = {};

  const eligible = AB_TEST_REGISTRY.filter(
    (abTest) => abTest.durability === "user" && !abTest.excludeForms?.has(formName ?? ""),
  );

  for (const abTest of eligible) {
    const value = getFlag(abTest.key);
    if (isAbTestVariant(value)) {
      assignments[abTest.key] = value;
    }
  }

  return Object.keys(assignments).length > 0 ? assignments : undefined;
}

/** @internal Test-only. Clears the global registry between test runs. */
export function resetAbTestRegistry(): void {
  AB_TEST_REGISTRY.length = 0;
}
